import { Modal, Table, Badge, Alert, Button } from "react-bootstrap";
import { useAppSelector } from "../../../../../redux/hooks";
import { ReducerEnum, setClientPortalModalType } from "../../../../../redux/reducer";
import { useContext } from "react";
import StateContext from "../../../context/State";
import { ClientPortalModalType, ClientPortalPage } from "../../../types";
import ConsentFormStep from '../../Initiate/NewClientPortal/ConsentFormStep';
import { useConsentFormConfiguration } from '../../../hooks/useConsentFormConfiguration';
import { useClientInformation } from '../../../hooks/useClientInformation';
import { useConsentFormInformationQuery } from '../../../hooks/useConsentFormInformationQuery';
import { validateConsentForm } from '../../../utils/documentValidation';
import { format } from 'date-fns';


export type ClientConsentFormTaskModalProps = {
    dispatch: (arg0: {
        type: ReducerEnum;
        payload: any;
        key?: string;
        extraPayload?: Record<string, string>;
    }) => void;
}

const ClientConsentFormTaskModal = (props: ClientConsentFormTaskModalProps) => {

    const { state, setState } = useContext(StateContext);

    const show: boolean = useAppSelector((state) => state.ClientPortalState?.ModalType === ClientPortalModalType.INFORMATION_SHARING_CONSENT);

    const { data: clients = [] } = useClientInformation(show);
    const { 
        isConsentFormRequired, 
        refetch: refreshConsentFormData 
    } = useConsentFormInformationQuery(show);

    const {
        consentFormState,
        setConsentFormState,
        setUploadedConsentFile,
        uploadedConsentFile,
        isSubmitting,
        handleSave,
        documentSavingPreference
    } = useConsentFormConfiguration({
        portalId: Number(state.portalId),
        appId: state.appId,
        setState: setState,
        onSuccess: refreshConsentFormData,
        onError: (error) => alert(error)
    });

    const handleSaveAndClose = async () => {
        // Validate consent form before saving
        const validation = validateConsentForm(consentFormState, uploadedConsentFile);
        if (!validation.isValid) {
            alert(validation.error);
            return; // Don't proceed if validation fails
        }
        
        props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE));
        
        setState({ ...state, page: ClientPortalPage.LOADING });
        
        try {
            await handleSave();
        } finally {
            setState({ ...state, page: ClientPortalPage.DASHBOARD, progressMessage: undefined });
        }
    };
    
    return (
        <>
            <Modal
                show={show}
                onHide={() => { props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE)) }}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                className="consent-form-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Information Sharing Consent Form Status</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {!isConsentFormRequired ? (
                        <>
                            <Alert>Consent Form not Required</Alert>
                            <ConsentFormStep
                                consentFormState={consentFormState}
                                setConsentFormState={setConsentFormState}
                                setUploadedConsentFile={setUploadedConsentFile}
                                documentSavingPreference={documentSavingPreference}
                            />
                        </>
                    ) : (
                        <div>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Status</th>
                                        <th>Date Submitted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients ?
                                        <>
                                            {clients.map((client, index) => (
                                                <tr key={index}>
                                                    <td>{client.Name}</td>
                                                    <td>{client.ConsentAcknowledged ? <Badge bg="success" text="dark" pill>Submitted</Badge> : <Badge bg="secondary" text="dark" pill>Not Submitted</Badge>} </td>
                                                    <td>
                                                        {client.ConsentAcknowledged && client.ConsentAcknowledgedDateTime ? (
                                                            <Badge
                                                                pill
                                                                text="dark"
                                                                bg="info"
                                                            >
                                                                {format(new Date(client.ConsentAcknowledgedDateTime), "MMMM do, yyyy 'at' h:mm a")}
                                                            </Badge>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                        :
                                        <></>
                                    }
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                
                { !isConsentFormRequired && <Modal.Footer>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveAndClose}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save & Notify Clients'}
                    </Button>
                </Modal.Footer>
                }
            </Modal>
        </>
    );
}

export default ClientConsentFormTaskModal;
